// @ts-strict-ignore
/**
 * A query-report-header component defines the saving
 * and loading mechanisms for interacting with reports
 */
import {ParamComponent} from 'panel';
import {h} from 'snabbdom';
import flattenDeep from 'lodash/flattenDeep';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';
import isString from 'lodash/isString';
import moment from 'moment';
import isEqual from 'lodash/isEqual';

import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {userDetailsStore} from 'common/report/stores/user-details';
import {currentProjectStore} from 'common/report/stores/project-details';
import {EntityType, OverrideModal} from 'common/report/util/saved-entities/types';
import {Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import {getCardInDashboardHref, getDashboardAllowedReports, getLastRefreshedTimeLabel} from 'common/report/util/boards';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {getReportNameFromSearchContext} from 'common/report/util/search';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {ContentDiscoveryType, SearchSource, SearchTrackedProperty} from 'common/report/util/search/tracking';
import {
  buildCreateEntityModalAttrs,
  getEditCapability,
  getEntityType,
  getEntityTypeName,
  getSharableEntityType,
  getShareCapability,
  promptSuperadminOverrideSave,
} from 'common/report/util/saved-entities';
import {dashboardUtils, isNestedDashboardsEnabled} from 'common/report/util/dashboards/utils';
import {parseStateFromUrl} from 'common/report/util/state-from-url';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {generateShortcutLabel, getCommandKey, KeyModifiers} from 'common/util/keyboard';
import {serializeAttributes} from 'common/util';
import {pluralize, truncate} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {REPORT_LOADED_COPY} from 'common/util/favicon';
import {promptSharing} from 'common/widgets/sharing';
import {
  FeatureGate,
  LocalStorageKey,
  Permission,
  ProjectFeatureFlag,
  ReportName,
  RestrictedReport,
  RestrictEntitySaving,
} from 'common/report/constants';
import {bookmarkClient} from 'common/report/queries/app-api/bookmark-client';
import {IconSizeType} from 'common/constants';
import {useStore} from 'common/util/component-helpers';
import {SharingAccessType} from 'common/widgets/sharing/types';
import {createEntityModalPromise} from 'common/widgets/create-entity-modal';
import {sharingUtils} from 'common/widgets/sharing/utils';
import {SharingModalDisabledReason, SharingModalViewType} from 'common/widgets/sharing/modal/types';
import {confirm} from 'common/components/confirm';
import {THEME} from 'common/components/confirm/types';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {IronContextRegistry} from 'common/report/context-registry';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {addToast} from 'common/components/toast-area/toast-manager';
import {StatusCode} from 'common/util/shared-client';
import {ToastType} from 'common/components/toast/types';
import {upsellModalPromise} from 'common/widgets/upsell-modal';
import {Placement} from 'common/components/tooltip/types';
import {ButtonTheme} from 'common/components/button/constants';
import {ContentActionType, Dashboard} from 'common/report/dashboards/types';
import {formatSamplingFactor, getReportUrl, isEmbedMode} from 'common/report/util';
import {reporter} from 'common/report/error-reporting';
import {savedReportLimitInfo, savedReportsLimitStore} from 'common/report/stores/saved-reports-limit';
import {promptSaveReportLimitModal} from 'common/widgets/create-entity-modal/saved-reports-limit-utils';
import {TrackingEventProperty} from 'common/widgets/user-list/tracking';
import {HistoryManager} from 'common/contexts/history-manager';
import {customAlertApi} from 'common/widgets/alerts/custom-alert/api';
import {ActivityMonitor} from 'common/util/activity-monitor';
import {getPresenceActivityMonitor} from 'common/report/queries/app-api/presence-client';
import {PresenceResults} from 'app_api/projects/presence/types';
import {loadEntityModalPromise} from 'common/widgets/load-entity-modal';
import {GlobalAccessType, ModalReturn} from 'common/widgets/create-entity-modal/types';
import {MixpanelUserItem} from 'common/widgets/user-list/types';
import {panel2react} from 'common/util/react';
import {incrementSaveAsNewReportName} from 'common/report/util/fast-report-iteration';
import {ReportEditorActions} from 'common/report/contexts/report-editor-actions';
import {getBoardsDashboardSectionItem} from 'common/report/util/boards/presets';
import {Directions} from 'common/components/drop-menu/types';
import {isBehaviorShowClause} from 'common/report/insights/models/utils';
import {ShowClause as ShowClauseType} from 'common/report/insights/util/types';
import {getLocation} from 'common/util/url';
import {getUpsellInfo, ModalTrigger} from 'common/widgets/in-product-upsell-modal/util';
import {promptInProductUpsellModal} from 'common/widgets/in-product-upsell-modal/prompt';
import {MetricType} from 'common/types/reports/bookmark';

import {HeaderAction, QueryReportHeaderParams, QueryReportHeaderSave, SaveModes, UntitledReportNameKey} from './types';
import {SaveTypeTrackingProperty, TrackingEvent} from './tracking';
import {template} from './template';
import alertsUpsellImage from './images/alerts-upsell-images.svg';

const RESET_ACTION = Object.freeze({
  icon: `new-report`,
  value: HeaderAction.Reset,
  label: ({t}) => t(`reports.actions.new`),
  elref: `reportNewButton`,
});

interface QueryReportHeaderState {
  existingEntityNames: string[];
  isBookmarkSelectorOpen: boolean;
  saveMode: SaveModes;
  openShareUponSave: boolean;
  showSaveBeforeShare: boolean;
  dashboardsLoading: boolean;
  isCreateAlertModalOpen: boolean;
  showPublicBadge: boolean;
  // boards
  isEditingTitle: boolean;
  isEditingDescription: boolean;
  newReportName: string;
  newReportDescription: string;
  dashboardsDropdownOpen: boolean;
  canSaveToMainDashboard: boolean;
  presenceViews: MixpanelUserItem[];
  subscribeMenuOpen: boolean;
  totalPresenceViews?: number;
  showSaveWarningModal: boolean;
  computedAt: string;
}

type JsxProps = {
  params: QueryReportHeaderParams;
  props?: {
    showCustomAlertModal?: boolean;
    extraTrackingParams?: Record<string, string>;
  };
  on?: {
    actionclick?: (ev: CustomEvent<{value: HeaderAction}>) => void;
    bookmarkselectoropen?: (ev: CustomEvent) => void;
    reset?: (ev: CustomEvent) => void;
    save?: (ev: CustomEvent<QueryReportHeaderSave>) => void;
    'back-to-board'?: (ev: CustomEvent<{clickEvent: MouseEvent}>) => void;
    bookmarkselect?: (ev: CustomEvent) => void;
    bookmarkselectorclose?: (ev: CustomEvent) => void;
    editreport?: (ev: CustomEvent) => void;
    update?: (ev: CustomEvent) => void;
  };
};

export class QueryReportHeader extends ParamComponent<
  QueryReportHeaderParams,
  QueryReportHeaderState,
  IronContextRegistry
> {
  // dashboard that contains at least one card with a link to the selected-bookmark
  public declare extraDashboards: DashboardOrigin[];
  // Used to check if saving/editing should trigger a warning because it affects Custom Alerts
  public customAlertsForSelectedBookmark = [];
  public customAlertsForProject = [];
  public declare dashboardController: DashboardSelectorController;
  private declare recentlyViewedDashboardsStorage: UserLocalStorageHelper<Dashboard[]>;
  private declare presenceActivityMonitor: ActivityMonitor;

  private handlePublicDashboardUpdate = (evt: CustomEvent) => {
    const numActiveLinks = evt.detail[`numLinks`];
    // Once there is an update to public dashboard in sharing modal
    // we ignore the initial attribute
    // hack, TODO: need to move this one level up
    this.setParams({...this.params, isPublicDashboard: false});
    this.update({showPublicBadge: numActiveLinks > 0});
  };
  private declare extraTracking: {[name: string]: Obj};

  get config() {
    return {
      defaultParams: {
        autoRefreshIntervalMs: null,
        canSave: false,
        canShare: false,
        canWrite: false,
        controlItems: [],
        dashboardsRoute: [],
        hideActions: [],
        invalidEntity: false,
        isSaveFailed: false,
        overflowControlItems: [],
        workspaceId: 0,
      },

      params: {
        allowStaffOverride: Boolean,

        // If set, creates an interval that fires a refresh event every x milliseconds.
        autoRefreshIntervalMs: Number,

        // Can the user change the currently selected entity?
        canSave: Boolean,

        // Can the user share the currently selected entity?
        canShare: Boolean,

        // permissions
        // Can the user create/change entities?
        canWrite: Boolean,

        computedAt: String,

        disableHeaderMargin: Boolean,

        // Additional set of items to be rendered in the control bar, interface ControlItem[]
        controlItems: Array,

        customAlertTimeUnit: String,
        customAlertsDisabledText: String,

        // display context for the load entity modal: insights, flows, dashboards, etc (SearchDisplayContext)
        // enum: Object.values(SearchDisplayContext)
        displayContext: String,

        // URL containing serialized report, used to create duplicate in a new tab.
        duplicateReportUrl: String,

        emptyEntityName: String,
        enableCustomAlertConditionTypes: Boolean,
        entitiesLoading: Boolean,

        // enum: Object.values(EntityType)
        entityType: String,

        hasUnsavedChanges: Boolean,

        // Hide a set of built-in header controls.
        hideActions: Array,

        // If entity is invalid, save will be prevented - create new or edit
        invalidEntity: Boolean,

        // Optional tooltip to display above disabled "Save" button when entity is invalid
        invalidMessage: String,

        // custom-alerts
        customAlertIntervalDays: Number,
        enableAnomalyDetectionControls: Boolean,
        bookmarkParams: Object,

        isCustomAlertsActionVisible: Boolean,
        isPublicDashboard: Boolean,
        isQuerySamplingActionVisible: Boolean,
        isQuerySamplingEnabled: Boolean,
        triggerQuerySamplingTourTooltip: Boolean,

        // Save report failed
        isSaveFailed: Boolean,

        isSuperadmin: Boolean,
        isTemplateDashboard: Boolean,
        isTemplateDraftDashboard: Boolean,

        // Board where the user comes from
        originDashboard: Object,

        // Items to be rendered in the overflow section of the control bar, interface ControlItem[]
        overflowControlItems: Array,

        // as of date for projects with data in the past.
        projectAsOfDate: String,

        projectId: Number,
        querySamplingDisabledMessage: String,
        resultsKeysAndValues: Array,

        // query sampling
        samplingFactor: Number,

        // Board's main dashboard
        selectedBookmarkDashboard: Object,

        selectedEntityCreator: Object,

        selectedEntityDescription: String,

        selectedEntityEditor: Object,
        // used to display who's online and offline
        onlineUsers: Array,

        selectedEntityId: Number,
        selectedEntityName: String,
        isSavingBookmark: Boolean,

        // If specified, dispatches '[mp-query-report-header] <event>' bubbled events that can be listened for tracking with tab=<track-name> as a property
        trackName: String,

        userId: Number,

        // sharing-disabled (visibility controls in create-entity-modal)
        // Message to display above visibility controls
        visibilityControlsMessage: String,

        workspaceId: Number,

        /** used to display "Saving..." or "You don't have permissions" messages, next to the title */
        boardsTitleBadgeEl: Object,
        dashboardsRoute: Array,
        // different parent components has different buttons to pass in query-report-header
        headerActionItemsEl: Object,
      },

      template,

      contexts: [
        `urlManager` as const,
        `localStorageManager` as const,
        `historyManager` as const,
        `dashboardSelectorController` as const,
        `currentProject` as const,
        `reportEditorActions` as const,
        `i18n` as const,
      ],

      defaultContexts: {
        urlManager: new DummyUrlManager(),
        localStorageManager: new DummyStorageManager(true),
        historyManager: new HistoryManager(),
        reportEditorActions: new ReportEditorActions(),
      },

      defaultState: {
        existingEntityNames: [],
        isBookmarkSelectorOpen: false,
        saveMode: null,
        openShareUponSave: false,
        // Represents the state where we need to show Save modal before Share modal
        showSaveBeforeShare: false,
        dashboardsLoading: false,
        isEditingTitle: false,
        isEditingDescription: false,
        isCreateAlertModalOpen: false,
        showPublicBadge: false,
        newReportName: null,
        newReportDescription: null,
        dashboardsDropdownOpen: false,
        canSaveToMainDashboard: false,
        presenceViews: [],
        computedAt: ``,
        showSaveWarningModal: false,
        subscribeMenuOpen: false,
      },

      hooks: {
        preUpdate: (_state: Partial<QueryReportHeaderState>, params?: Partial<QueryReportHeaderParams>) => {
          if (!params) return;
          if (`selectedEntityName` in params) {
            this.helpers.updateDocumentTitle(params.selectedEntityName);
            this.update({newReportName: null});
          }

          if (params.selectedEntityDescription !== this.params.selectedEntityDescription) {
            this.update({newReportDescription: null});
          }

          if (
            params.selectedEntityId !== this.params.selectedEntityId ||
            !isEqual(params.originDashboard, this.params.originDashboard)
          ) {
            this.loadDashboardDataController(params.selectedEntityId, params.originDashboard, true);
            this.loadCustomAlerts(params.selectedEntityId);
          }

          if (params.computedAt) {
            this.update({computedAt: params.computedAt});
          }

          const mpSharingModalElement = document.querySelector(`mp-sharing-modal`);
          if (params.isSaveFailed !== this.params.isSaveFailed && this.params.isSaveFailed && mpSharingModalElement) {
            mpSharingModalElement.removeAttribute(`open`);
          }
          // In case where a user saves a new report or saves a report as a duplicate
          if (params.selectedEntityId !== this.params.selectedEntityId || this.params.selectedEntityId === null) {
            const newEntityId = params.selectedEntityId;
            if (newEntityId && this.state.openShareUponSave) {
              // no need to disable the sharing modal when a user is the creator except for consumer as creator
              const userIsConsumer = this.isUserConsumer();
              promptSharing({
                type: this.helpers.getSharableEntityType(),
                id: newEntityId,
                'project-id': this.params.projectId,
                disabled: userIsConsumer,
                'disabled-reason': userIsConsumer ? this.t(SharingModalDisabledReason.Consumer) : null,
              });
              this.update({
                openShareUponSave: false,
              });
            }

            this.resetPresenceMonitor(newEntityId, params.entityType ?? this.params.entityType);
          }

          // someone enters / leaves to room
          if (
            params.onlineUsers &&
            this.params.onlineUsers &&
            !isEqual(
              params.onlineUsers.map((u) => u.id),
              this.params.onlineUsers.map((u) => u.id),
            )
          ) {
            this.resetPresenceMonitor(params.selectedEntityId, params.entityType ?? this.params.entityType);
          }
        },
      },

      helpers: {
        IconSizeType,
        serializeAttributes,
        propagateEvent: (ev) => propagateEvent(this, ev),
        userIsEntityOwner: (entity): boolean => this.params.userId === entity.userId,
        userIsStaff: (): boolean => userDetailsStore.value.isStaff,
        hasSlot: (name) => this.hasSlot(name),
        getHeaderActionOverflowTooltip: () => {
          const exampleActions = flattenDeep(this.getHeaderActionOverflowItems().map((item) => item.items))
            .slice(0, 2)
            .map((item) => item.label)
            .concat(this.t(`common.overflow-more`));
          return this.getContext(`i18n`).formatList(exampleActions) + `...`;
        },
        getShareCapability: () => {
          // query-report-header gets entity object destructured through attrs
          const entity = {
            canShare: this.params.canShare,
            canUpdateBasic: this.params.canSave,
            allowStaffOverride: this.params.allowStaffOverride,
            isSuperadmin: this.params.isSuperadmin,
          };
          return getShareCapability(entity, EntityType.Report, {
            isSharingEnabled: this.helpers.isSharingEnabled(),
          });
        },
        allowSuperAdminOverride: () =>
          /** Allow super admin override for an entity that is valid but not save-able (due to permission) and user is a superadmin */
          (!this.params.canSave && !this.params.invalidEntity && this.params.isSuperadmin) ||
          this.params.allowStaffOverride,
        handleSharingStatusButtonClick: async () => {
          // Open save modal when share is clicked in brand new report - only for share enabled projects
          if (this.helpers.shouldOpenSaveModalOnShare() && this.helpers.isSharingEnabled()) {
            this.update({
              openShareUponSave: true,
              showSaveBeforeShare: true,
            });
            this.openSaveModal(SaveModes.Save, SaveTypeTrackingProperty.Existing);
          } else {
            const sharingAccessType = this.helpers.getUserSharingAccessType();
            await promptSharing({
              type: this.helpers.getSharableEntityType(),
              id: this.params.selectedEntityId,
              'project-id': this.params.projectId,
              disabled: sharingAccessType === SharingAccessType.Viewer || sharingAccessType === SharingAccessType.None,
              'entity-name': this.params.selectedEntityName,
              'disabled-reason': this.t(sharingUtils.getSharingModalDisabledReasonKey(sharingAccessType)),
              'is-superadmin': this.helpers.allowSuperAdminOverride(),
              'entity-owner-id': this.params.selectedEntityCreator?.id,
            });
          }
        },
        handleViewPublicDashboardLinksClick: () => {
          const sharingAccessType = this.helpers.getUserSharingAccessType();
          promptSharing({
            type: this.helpers.getSharableEntityType(),
            id: this.params.selectedEntityId,
            'project-id': this.params.projectId,
            disabled: sharingAccessType === SharingAccessType.Viewer || sharingAccessType === SharingAccessType.None,
            'disabled-reason': this.t(sharingUtils.getSharingModalDisabledReasonKey(sharingAccessType)),
            'open-as': SharingModalViewType.PublicDashboardDetails,
            'entity-name': this.params.selectedEntityName,
          });
        },
        handleBoardButtonClick: () => {
          this.track(TrackingEvent.ReturnToBoard, {Origin: `Back Button`});
          this.dispatchEvent(new CustomEvent(`back-to-board`));
        },
        handleBoardsSaveMenuChange: ({state}) => {
          if (state === `open`) {
            this.track(TrackingEvent.StartSave);
          }
        },
        /**
         *
         * @returns the dashboard where the report is associated. `origin-dashboard` is used when a new report is created
         */
        getBoardsMainDashboard: () => {
          return this.params.selectedBookmarkDashboard || this.getDecodedOriginDashboard();
        },
        /**
         *
         * @returns the dashboard where the user come from, and where we should redirect when the `back` arrow is clicked. If no `origin-dahsboard` is specified, we use the report's main dashboard
         */
        getBoardsRedirectDashboard: () => {
          return this.getDecodedOriginDashboard() || this.params.selectedBookmarkDashboard;
        },
        handleSaveWarningTableCellCliked: ({detail}) => {
          if (detail.context.field === `name`) {
            this.openCustomAlertPage();
          }
        },
        saveWarningModalChanged: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({showSaveWarningModal: false});
          }
        },
        saveWarningModalSubmitted: (ev) => {
          if (ev.detail.action === `confirm`) {
            this.saveReport(SaveModes.Save);
          }
        },
        saveReportToDashboard: async (saveMode: SaveModes, dashboardId?: number) => {
          await savedReportsLimitStore.refresh({projectId: this.params.projectId});
          const {isBelowLimit, hasSavedReportLimit} = savedReportLimitInfo(this.params.projectId);
          if (hasSavedReportLimit) {
            const currentProject = this.getContext(`currentProject`).get()?.value;
            if (
              getUpsellInfo(currentProject, ModalTrigger.SAVE_REPORTS, this.getContext(`i18n`)) &&
              (saveMode === SaveModes.SaveNew || !this.hasSelectedEntity)
            ) {
              await promptInProductUpsellModal(ModalTrigger.SAVE_REPORTS);
              return;
            } else {
              // show how many saved reports exist no matter what
              await promptSaveReportLimitModal(
                this.params.projectId,
                this.getContext(`i18n`),
                saveMode === SaveModes.SaveNew,
              );
              // reject the rest of the process if wants to save a new report over the report limit
              if (!isBelowLimit && saveMode === SaveModes.SaveNew) {
                return;
              }
            }
          }

          const dashboards = this.dashboardController.getDashboards();
          const dashboard = dashboards.find(({id}) => id === dashboardId) || this.helpers.getBoardsMainDashboard();
          if (!isEmpty(dashboard)) {
            // verify user confirmed saving if need be
            // if current bookmark IS part of dashboards or alerts, warn user via dialog
            let confirmedSave = true;
            if (
              saveMode !== SaveModes.SaveNew &&
              (this.getNumExtraDashboards() > 0 || this.getNumCustomAlertsForSelectedBookmark() > 0)
            ) {
              if (this.getNumCustomAlertsForSelectedBookmark()) {
                this.update({showSaveWarningModal: true});
                return;
              } else {
                confirmedSave = await confirm({
                  theme: THEME.CONFIRM,
                  'confirm-button-label': `Save`,
                  title: `Save Report`,
                  description: this.helpers.getEditWarning(),
                });
              }
            }
            if (confirmedSave) {
              this.saveReport(saveMode, dashboard);
            }
          }
        },
        handleAlertLimitUpsellButtonClicked: () => {
          return upsellModalPromise(
            {
              header: `You've exceeded your Real Time Alert limit`,
              description: `You’ve used 5 or more alerts in your project. To use unlimited alerts, upgrade to an Enterprise plan. With an Enterprise plan, you’ll also get:`,
              'feature-gate-type': FeatureGate.MaxCustomAlerts,
              open: true,
              closeable: true,
              'callout-top': `Anomaly Detection alerts`,
              'callout-middle': `Root Cause Analysis to resolve data issues quickly`,
              'callout-bottom': `Dynamic data views, warehouse connectors, & more…`,
              'upgrade-url': `/contact-us/sales/?utm_source=alert_limit_gate`,
              'upgrade-text': `Contact Sales`,
            },
            {backgroundImage: alertsUpsellImage},
          );
        },
        handleAlertUpsellButtonClicked: () => {
          if (
            getUpsellInfo(this.getContext(`currentProject`).get()?.value, ModalTrigger.ALERTS, this.getContext(`i18n`))
          ) {
            return promptInProductUpsellModal(ModalTrigger.ALERTS);
          }
          return upsellModalPromise(
            {
              header: `Real Time Alerts`,
              description: `Whether cart abandonment spiked or there's been a drop in user activation, Mixpanel can flag it for you. Control exactly when you're notified by selecting the reports to monitor and the thresholds that trigger alerts.`,
              'feature-gate-type': FeatureGate.AllowCustomAlerts,
              'learn-more-button-url': `/dashboards-alerts`,
              open: true,
              closeable: true,
              'upgrade-text': `Upgrade`,
            },
            {backgroundImage: alertsUpsellImage},
          );
        },
        handleCustomAlertCreated: () => {
          addToast({
            cta: `Manage Alerts`,
            ctaHref: getReportUrl(`settings#account/alert-settings`, {
              projectId: this.params.projectId,
            }),
            message: `Custom alert created.`,
            ctaTarget: `_blank`,
          });
          this.helpers.closeCustomAlertModal();
          this.loadCustomAlerts();
        },
        handleCustomAlertError: (ev) => {
          let reason = ``;
          if (ev.detail.status === StatusCode.PermissionDenied) {
            if (ev.detail.error) {
              reason = `, ${ev.detail.error}`;
            } else {
              reason = `, Unauthorized`;
            }
          }
          addToast({
            message: `Failed to create custom alert${reason}.`,
            type: ToastType.ERROR,
          });
          this.helpers.closeCustomAlertModal();
        },
        closeCustomAlertModal: () => {
          this.update({isCreateAlertModalOpen: false});
        },
        shouldOpenSaveModalOnShare: (): boolean => !this.params.selectedEntityId && !this.hasSelectedEntity,

        bookmarkSelected: (ev) => {
          this.dispatchEvent(new CustomEvent(`bookmarkselect`, {detail: ev.detail}));
        },

        // Set document title to report title
        updateDocumentTitle: (reportTitle = this.params.selectedEntityName) => {
          const projectName = this.project?.name;

          // if the title is report loaded copy, we should not override this
          if (document.title === REPORT_LOADED_COPY) {
            return;
          }

          let newTitle;
          if (reportTitle && projectName) {
            newTitle = `${reportTitle} - ${projectName} / Mixpanel`;
          } else if (reportTitle) {
            newTitle = `${reportTitle} / Mixpanel`;
          } else if (projectName) {
            newTitle = `${projectName} / Mixpanel`;
          } else {
            newTitle = `Mixpanel`;
          }

          if (newTitle !== document.title) {
            document.title = newTitle;
          }
        },

        submitSaveModal: (result: ModalReturn, saveType: SaveTypeTrackingProperty) => {
          if (result.event === `saveAndShare`) {
            // When Share Management is clicked in Save Modal, we need to open Share modal
            this.update({
              openShareUponSave: true,
            });
          }
          //save and share has a `.value` property, otherwise the report name and description is on the object
          this.submitSave({newReportData: result.value ?? result, saveType});
        },

        getDashboardSelectorExcludedDashboards: () => {
          const excludedDashboards = this.extraDashboards.map((dash) => dash.id);
          excludedDashboards.push(this.helpers.getBoardsMainDashboard()?.id);
          return excludedDashboards;
        },

        getSamplingNotice: ({isReportModeEnabled = true} = {}) => {
          const samplingFactor = this.params.samplingFactor;
          if (samplingFactor && samplingFactor < 1) {
            const i18n = this.getContext(`i18n`);
            const samplingPercentage = `${formatSamplingFactor(samplingFactor)}`;
            if (isReportModeEnabled) {
              return i18n.t(`sampling.report-mode-sampling-notice`);
            } else {
              return i18n.t(`sampling.sampled-at`, {
                samplingPercentage,
              });
            }
          }

          return null;
        },

        onOverflowMenuClick: () => {
          this.track(TrackingEvent.OverflowMenuClicked, {'Display Context': this.params.displayContext});
          this.update();
        },

        onHeaderActionClick: async (detail) => {
          switch (detail.value) {
            case HeaderAction.Rename:
              if (this.isReportModeEnabled() || this.isCanvasModeEnabled() || this.isExperimentModeEnabled()) {
                this.update({isEditingTitle: true});
              } else {
                this.openSaveModal(SaveModes.Edit, SaveTypeTrackingProperty.Renamed);
              }
              this.track(TrackingEvent.StartReportRename, {'Rename Location': `Overflow`});
              break;
            case HeaderAction.Load:
              this.openBookmarkSelector();
              break;
            case HeaderAction.SaveNew:
              if (!this.hasSelectedEntity) {
                // if no bookmark is currently selected, show bookmark edit modal
                this.openSaveModal(SaveModes.Save);
              } else {
                this.openSaveModal(SaveModes.SaveNew);
              }
              break;
            case HeaderAction.Save:
              await this.saveFromHeaderAction();
              break;
            case HeaderAction.Reset:
              this.handleResetAction();
              break;
            case HeaderAction.CreateAlert:
              if (this.helpers.exceedsMaxCustomAlerts()) {
                const numAlerts = this.customAlertsForProject.length;
                const metadata = {currentCustomAlerts: numAlerts};

                if (
                  getUpsellInfo(
                    this.getContext(`currentProject`).get()?.value,
                    ModalTrigger.ALERTS,
                    this.getContext(`i18n`),
                    metadata,
                  )
                ) {
                  promptInProductUpsellModal(ModalTrigger.ALERTS, metadata);
                  break;
                }
              }
              this.update({isCreateAlertModalOpen: true});
              break;
            case HeaderAction.ManageAlert:
              this.update({isCreateAlertModalOpen: false});
              this.openCustomAlertPage();
              break;
            case HeaderAction.EnableAlerts:
              this.helpers.handleAlertUpsellButtonClicked();
              break;
            case HeaderAction.IncreaseAlertLimit:
              this.helpers.handleAlertLimitUpsellButtonClicked();
              break;
            case HeaderAction.QuerySampling:
              this.helpers.handleToggleQuerySampling(detail);
              break;
            case HeaderAction.Delete:
              this.track(TrackingEvent.StartReportDelete);
              this.dispatchEvent(new CustomEvent(`actionclick`, {detail}));
              break;
            case HeaderAction.Undo:
              this.getContext(`historyManager`).undo();
              break;
            case HeaderAction.Redo:
              this.getContext(`historyManager`).redo();
              break;
            case HeaderAction.Duplicate:
              if (this.isReportModeEnabled()) {
                this.trackDuplicateButtonClick();
              } else {
                this.dispatchEvent(new CustomEvent(`actionclick`, {detail}));
              }
              break;
            default:
              this.dispatchEvent(new CustomEvent(`actionclick`, {detail}));
              break;
          }
          // when ev.detail.name === move, the ev.detail.value is a number and we instead want to use the ev.detail.name as the selected action for tracking
          this.track(TrackingEvent.OverflowMenuActionSelected, {
            'Selected Action': detail.name === ContentActionType.Move ? detail.name : detail.value,
            'Display Context': this.params.displayContext,
          });
        },

        getEditWarning: () => {
          const numExtraDashboards = this.getNumExtraDashboards();
          const numAlerts = this.getNumCustomAlertsForSelectedBookmark();

          if (!numExtraDashboards && !numAlerts) {
            return ``;
          }

          // another Dashboard or 2 other Dashboards
          const numDashboardsText = numExtraDashboards
            ? `${numExtraDashboards === 1 ? `another` : `${numExtraDashboards} other`} ${pluralize(
                `Board`,
                numExtraDashboards,
              )}`
            : ``;
          // an Alert or 2 Alerts
          const numAlertsText = numAlerts
            ? `${numAlerts === 1 ? `an` : numAlerts} ${pluralize(`Alert`, numAlerts)}`
            : ``;
          const conjunction = numAlerts && numExtraDashboards ? ` and ` : ``;

          const alertText = numAlerts > 1 ? `alerts` : `alert`;
          const pointerText = numAlerts > 1 ? `these` : `this`;
          if (numAlertsText) {
            const boardActiveText = numDashboardsText ? ` and is active on ` + numDashboardsText : ``;
            return `This report is currently used to query ${pointerText} ${alertText}${boardActiveText}. Saving changes to the report may affect the ${alertText} output or cause the ${alertText} to stop running. If you wish to avoid invalidating ${pointerText} ${alertText}, please save as new report.`;
          }
          return [
            `This report is currently active on`,
            ` ${numAlertsText}${conjunction}${numDashboardsText}.`,
            ` By continuing, you’ll be updating the data across all instances.`,
          ].join(``);
        },

        buildCreateEntityModalAttrs: () => {
          const isDuplicating = this.state.saveMode === SaveModes.SaveNew;
          const isEditing = this.hasSelectedEntity && !isDuplicating;
          const editWarningMessage = this.helpers.getEditWarning();
          const showEditWarning = !!editWarningMessage;
          const i18n = this.getContext(`i18n`);

          return buildCreateEntityModalAttrs({
            modalTitleOverride: this.state.showSaveBeforeShare
              ? i18n.t(`components.create-entity.save-before-sharing`)
              : null,
            modalOpen: true,
            modalCloseable: this.params.entityType === EntityType.Dashboard,
            isEditing,
            isDuplicating,
            projectId: this.params.projectId,
            entityType: this.params.entityType,
            // showSaveBeforeShare represents if a Save modal was triggered by the Share button.
            // We show Share Management button in the Save modal only when Save modal was triggered by Save button
            showShareManagement: this.helpers.isSharingEnabled() && !this.state.showSaveBeforeShare,
            entity: {
              name: this.params.selectedEntityName,
              description: this.params.selectedEntityDescription,

              // permissions
              can_view: this.params.isSuperadmin || this.params.canSave,
              can_update_basic: this.params.canSave,
              can_share: this.params.canShare,
              is_superadmin: this.params.isSuperadmin,
              allow_staff_override: this.params.allowStaffOverride,
            },
            editWarning: showEditWarning && editWarningMessage,
            // Show copy link button on Save Before Share stage
            showCopyLinkButton: this.state.showSaveBeforeShare,
            i18n,
          });
        },

        canUserShare: () => this.helpers.getShareCapability().enableShare,
        getUserSharingAccessType: () => this.helpers.getShareCapability().sharingAccessType,
        isShareButtonEnabled: () => {
          const selectedBookmarkId = this.params.selectedEntityId;
          // When a user loads a report and makes changes, the URL loses bookmark-id
          // in case a user either refreshes the page OR share the modified URL
          // we lose bookmark-id but app thinks we have selected a report
          // in this case we should disable share button as all share APIs will fail w/o bookmark-id
          if (this.hasSelectedEntity && !selectedBookmarkId) {
            return false;
          } else if (this.params.invalidEntity) {
            return false;
          } else if (!this.hasSelectedEntity && !selectedBookmarkId && !this.params.invalidEntity) {
            // Share button should be enabled for unsaved reports
            return true;
          } else if (!this.helpers.canUserShare()) {
            // Viewer/Consumer is not allowed to share, but should be able to access Viewer version of Share Modal
            return this.helpers.getUserSharingAccessType() !== SharingAccessType.Editor;
          } else {
            return true;
          }
        },

        getShareButtonTheme: () => {
          if (
            this.isReportModeEnabled() ||
            this.isDashboardModeEnabled() ||
            this.isCanvasModeEnabled() ||
            this.isExperimentModeEnabled()
          ) {
            return ButtonTheme.Tertiary;
          }
          return ButtonTheme.Primary;
        },

        showShareButton: () => {
          const sharableEntityType = this.helpers.getSharableEntityType();
          if (!sharableEntityType) {
            return false;
          }
          const {appName} = parseStateFromUrl(getLocation().pathname) || {};
          const isUsersPage = this.params.entityType === EntityType.Report && appName === ReportName.Users;
          if (isUsersPage) {
            // TODO this logic is duplicated in mp-context, figure out how to consolidate
            return (
              (currentProjectStore.value?.hasAccessToReport(RestrictedReport.Cohorts) ||
                currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.Cohorts)) &&
              currentProjectStore.value?.canSaveEntity(RestrictEntitySaving.Cohorts)
            );
          }
          return true;
        },

        isSharingEnabled: () => {
          return currentProjectStore.value?.getFeatureGate(FeatureGate.Sharing);
        },

        getSharableEntityType: () => this.params.entityType && getSharableEntityType(this.params.entityType),
        getTitle: () => {
          if (this.isDashboardModeEnabled()) {
            // length of the empty space for the title
            let truncatedLength = 120;
            // make space for the view only copy on the right
            if (!this.canSave() && this.hasUnsavedChanges) {
              truncatedLength = 99;
            }
            return (
              truncate(this.state.newReportName || this.params.selectedEntityName, truncatedLength) ||
              this.params.emptyEntityName
            );
          }
          return this.state.newReportName || this.params.selectedEntityName || this.params.emptyEntityName;
        },

        getProjectTimezone: () => {
          return currentProjectStore.value?.timezone;
        },

        getShareTooltipText: () => {
          const shareActionItem = this.getShareButtonAction();
          return shareActionItem.tooltip?.text ?? ``;
        },

        handleAutoRefresh: () => {
          // Clear the computedAt value to dismiss the refresh button during loading.
          this.update({computedAt: ``});
          this.dispatchEvent(
            new CustomEvent(`actionclick`, {detail: {value: HeaderAction.Refresh, useQueryApiCache: true}}),
          );
        },
        handleToggleQuerySampling: (detail) => {
          this.dispatchEvent(new CustomEvent(`actionclick`, {detail: {...detail, value: HeaderAction.QuerySampling}}));
          addToast({
            message: this.params.isQuerySamplingEnabled
              ? this.t(`sampling.query-sampling.disable-success`)
              : this.t(`sampling.query-sampling.enable-success`),
            type: ToastType.SUCCESS,
          });
        },
        handleDismissQuerySamplingTour: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`actionclick`, {detail: {...ev.detail, value: HeaderAction.DismissQuerySamplingTour}}),
          );
        },
        isReportModeEnabled: () => this.isReportModeEnabled(),
        isDashboardModeEnabled: () => this.isDashboardModeEnabled(),
        isCanvasModeEnabled: () => this.isCanvasModeEnabled(),
        isExperimentModeEnabled: () => this.isExperimentModeEnabled(),
        isHeaderReady: () => !!currentProjectStore?.value,
        handleEditReportName: () => {
          this.track(TrackingEvent.StartReportRename, {'Rename Location': `Title`});
          this.update({isEditingTitle: true});
        },
        handleEditReportDescription: () => {
          this.track(TrackingEvent.StartReportEditDescription);
          this.update({isEditingDescription: true});
        },
        isAlertFeatureGateEnabled: () => {
          return currentProjectStore.value?.getFeatureGate(FeatureGate.AllowCustomAlerts);
        },
        isAlertsDisabled: () => {
          return (
            this.params.bookmarkParams &&
            this.params.bookmarkParams.sections.show.some(
              (showClause: ShowClauseType) =>
                isBehaviorShowClause(showClause) &&
                (showClause.behavior.type === MetricType.Retention ||
                  showClause.behavior.type === MetricType.RetentionFrequency),
            )
          );
        },
        exceedsMaxCustomAlerts: () => {
          const numAlerts = this.customAlertsForProject.length;
          const maxAlerts = this.getContext(`currentProject`).get()?.value?.getFeatureGate(FeatureGate.MaxCustomAlerts);
          return numAlerts >= maxAlerts;
        },
        isReportSaved: () => Boolean(this.params.selectedEntityId) && !this.hasUnsavedChanges,
        isAlertButtonDisabled: () => {
          return (
            !this.helpers.isReportSaved() ||
            Boolean(this.params.customAlertsDisabledText) ||
            this.helpers.isAlertsDisabled()
          );
        },
        getAlertsTooltipText: () => {
          if (this.helpers.isAlertsDisabled()) {
            return this.t(`reports.actions.retention-alerts-disabled`);
          }
          if (!this.helpers.isReportSaved()) {
            return this.t(`reports.actions.alerts-need-save`);
          }

          return this.params.customAlertsDisabledText || this.t(`reports.actions.create-alert`);
        },
        getSaveAction: () => {
          const saveTooltipText = this.getSaveTooltipText();
          return {
            icon: `save`,
            label: this.t(`reports.actions.saving.save`),
            tooltip: saveTooltipText ? {text: saveTooltipText} : null,
            value: HeaderAction.Save,
            elref: `reportSaveButton`,
            isDisabled: this.isLegacySaveActionDisabled(),
          };
        },
        getReportName: () => {
          return this.state.newReportName === null
            ? this.params.selectedEntityName || this.t(UntitledReportNameKey)
            : this.state.newReportName;
        },
        getReportDescription: () => {
          return isString(this.state.newReportDescription)
            ? this.state.newReportDescription
            : this.params.selectedEntityDescription;
        },
        showViewOnlyTextHighlight: () => {
          return this.hasSelectedEntity && !this.canEditExistingEntity();
        },
        viewOnlyHighlightMarkdown: () => {
          return `View only`;
        },
        isFreePlan: (): boolean => {
          return currentProjectStore.value?.eventsPlanName === `Free`;
        },
      },
    };
  }

  private async saveFromHeaderAction() {
    if (!this.hasSelectedEntity) {
      // if no bookmark is currently selected, show bookmark edit modal
      this.openSaveModal(SaveModes.Save);
    } else if (this.getNumExtraDashboards() || this.getNumCustomAlertsForSelectedBookmark()) {
      // if current bookmark IS part of dashboards or alerts, warn user via dialog
      confirm({
        theme: THEME.CONFIRM,
        'confirm-button-label': `Save`,
        title: `Save Report`,
        description: this.helpers.getEditWarning(),
      }).then((confirmed) => {
        if (confirmed && this.hasUnsavedChanges) {
          this.submitSave({});
        }
      });
    } else if (this.helpers.allowSuperAdminOverride()) {
      const modalOutcome = await promptSuperadminOverrideSave(getEntityType(this.helpers.getSharableEntityType()), {
        i18n: this.getContext(`i18n`),
      });
      if (modalOutcome === OverrideModal.Copy) {
        this.openSaveModal(SaveModes.SaveNew);
      } else if (modalOutcome !== OverrideModal.Closed && this.hasUnsavedChanges) {
        this.submitSave({}); // save directly
      }
    } else if (this.hasUnsavedChanges) {
      this.submitSave({});
    }
  }

  get print() {
    return this.getContext(`i18n`).print;
  }

  public connectedCallback() {
    super.connectedCallback();

    document.addEventListener(`PublicDashboardUpdated`, this.handlePublicDashboardUpdate);
    // update header when user details are updated (from backend or otherwise)
    const userStoreCallback = () => this.helpers.updateDocumentTitle();
    useStore(this, userDetailsStore, userStoreCallback).fetch();

    const projectStoreCallback = () => {
      if (!this.presenceActivityMonitor) {
        this.resetPresenceMonitor(this.params.selectedEntityId, this.params.entityType);
      }
      this.update();
    };
    useStore(this, currentProjectStore, projectStoreCallback).fetch();

    if (this.isReportModeEnabled()) {
      useStore(this, savedReportsLimitStore, () => this.update()).fetch({projectId: this.params.projectId});
    }

    this.recentlyViewedDashboardsStorage = new UserLocalStorageHelper({
      namespace: LocalStorageKey.RecentlyViewedDashboards,
      context: this.getContext(`localStorageManager`),
    });

    this.dashboardController = this.getContext(`dashboardSelectorController`);
    this.loadDashboardDataController();
    this.loadCustomAlerts();

    this.getContext(`reportEditorActions`).setOpenTitleEditorCallback(() => this.openTitleEditorCallback());

    this.setSuperProperties({'Has Boards UI Test': true});
  }

  public disconnectedCallback() {
    // Reset document title as page navigates away
    this.helpers.updateDocumentTitle(``);
    document.removeEventListener(`PublicDashboardUpdated`, this.handlePublicDashboardUpdate);
    this.presenceActivityMonitor?.stop();
    super.disconnectedCallback();
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  openTitleEditorCallback() {
    this.update({isEditingTitle: true});
  }

  saveReport(saveMode, dashboard = null) {
    let newReportName: string;
    if (saveMode === SaveModes.SaveNew) {
      newReportName = incrementSaveAsNewReportName(this.helpers.getReportName());
    }

    this.update({saveMode, ...(newReportName ? {newReportName} : {})});
    this.submitSave({
      newReportData: {
        name: this.helpers.getReportName(),
        description: this.helpers.getReportDescription(),
      },
      dashboard: dashboard || this.helpers.getBoardsMainDashboard(),
    });
  }

  get hasSelectedEntity() {
    return Boolean(this.params.selectedEntityId);
  }

  get hasUnsavedChanges() {
    // if there is no entity selected/saved, then by definition there are unsaved changes
    return (
      !this.hasSelectedEntity ||
      this.params.hasUnsavedChanges ||
      (this.isReportModeEnabled() &&
        (this.params.selectedEntityName !== this.helpers.getReportName() ||
          this.params.selectedEntityDescription !== this.helpers.getReportDescription()))
    );
  }

  get project() {
    return userDetailsStore.value.projects[this.params.projectId] || null;
  }

  set existingEntityNames(existingEntityNames) {
    this.update({existingEntityNames});
  }

  set extraTrackingParams(val: {[name: string]: Obj}) {
    this.extraTracking = val;
    this.update();
  }

  get extraTrackingParams(): {[name: string]: Obj} {
    return this.extraTracking;
  }

  private _triedToShowCustomAlertModal = false;

  set showCustomAlertModal(val: boolean) {
    // only try one time
    if (val && !this._triedToShowCustomAlertModal) {
      this._triedToShowCustomAlertModal = true;
      this.tryToShowCreateAlertDialog();
    }
  }

  private getNumExtraDashboards() {
    return this.extraDashboards?.length;
  }

  private getNumCustomAlertsForSelectedBookmark() {
    return this.customAlertsForSelectedBookmark.length;
  }

  private async loadCustomAlerts(selectedEntityId?: number) {
    selectedEntityId = selectedEntityId || this.params.selectedEntityId;
    if (this.hasSelectedEntity && this.params.isCustomAlertsActionVisible) {
      try {
        [this.customAlertsForProject, this.customAlertsForSelectedBookmark] = await Promise.all([
          customAlertApi(this.params.projectId, this.params.workspaceId).loadCustomAlerts({skip_user_filter: true}),
          customAlertApi(this.params.projectId, this.params.workspaceId).loadCustomAlertsForBookmark(selectedEntityId),
        ]);
      } catch (error) {
        reporter.error(error as Error, `Failed to get Custom Alerts`);
      }
    }
  }

  /**
   * Checks for presence of a slotted child element
   */
  private hasSlot(slotName: string): boolean {
    return !!this.querySelector(`[slot="${slotName}"]`);
  }

  private openBookmarkSelector() {
    this.dispatchEvent(new CustomEvent(`bookmarkselectoropen`));

    loadEntityModalPromise({
      'entities-loading': this.params.entitiesLoading,
      'entity-type-filter': this.params.displayContext,
      'project-id': this.params.projectId.toString(),
      'user-id': this.params.userId,
      'workspace-id': this.params.workspaceId,
      'filter-default-open': true,
    }).then((result) => {
      if (result.type === `close`) {
        this.dispatchEvent(new CustomEvent(`bookmarkselectorclose`));
      } else if (result.type === `select`) {
        this.helpers.bookmarkSelected(result);
      }
    });

    this.track(TrackingEvent.ReportListOpen, {
      [SearchTrackedProperty.ContentDiscoveryType]: ContentDiscoveryType.Browse,
      [SearchTrackedProperty.Source]: SearchSource.Modal,
    });
  }

  handleResetAction() {
    if (!this.helpers.isReportModeEnabled()) {
      this.dispatchEvent(new CustomEvent(`reset`));
    }
    // creates a new report in a new tab but don't reset the current report (for board reports only)
  }

  private openSaveModal(saveMode: SaveModes, saveType?: SaveTypeTrackingProperty) {
    this.update({
      saveMode,
    });
    createEntityModalPromise(this.helpers.buildCreateEntityModalAttrs(), {
      existingEntityNames: this.state.existingEntityNames,
    }).then((result) => {
      if (result.didSave) {
        this.helpers.submitSaveModal(result, saveType);
      } else {
        this.closeSaveModal(result);
      }
    });
  }

  private closeSaveModal(result) {
    // When Cancel button is clicked inside the Save modal triggered by the Share button
    // we need to clear openShareUponSave state in order to prevent the Share modal
    // showing up in subsequent Save Modal unexpectedly
    const clearOpenShareUponSave = result.clearOpenShareUponSave;
    this.update({
      saveMode: null,
      showSaveBeforeShare: false,
      openShareUponSave: clearOpenShareUponSave ? false : this.state.openShareUponSave,
    });
  }

  autoSaveNameAndDescription() {
    if (this.helpers.isDashboardModeEnabled() && this.hasSelectedEntity) {
      this.dispatchEvent(
        new CustomEvent(`save`, {
          detail: {
            description: this.helpers.getReportDescription(),
            didSave: true,
            event: `save`,
            saveAsNew: false,
          },
        }),
      );
    } else if (this.hasSelectedEntity) {
      this.helpers.saveReportToDashboard(SaveModes.Save, this.helpers.getBoardsMainDashboard()?.id);
    }
  }

  handleReportTitleChanged(value: string) {
    const prevReportName = this.helpers.getReportName();

    if (!value.trim()) {
      // empty name should default to Untitled
      this.update({isEditingTitle: false, newReportName: this.t(UntitledReportNameKey)});
    } else {
      this.update({isEditingTitle: false, newReportName: value});
    }
    if (!this.hasSelectedEntity) {
      this.dispatchEvent(
        new CustomEvent(`editreport`, {
          detail: {name: this.state.newReportName},
        }),
      );
    }
    if (prevReportName !== this.state.newReportName) {
      this.autoSaveNameAndDescription();
    }
  }

  submitSave({
    newReportData = null,
    saveType,
    dashboard,
  }: {
    newReportData?: {
      didSave?: boolean;
      event?: HeaderAction;
      name?: string;
      description?: string;
      globalAccessType?: GlobalAccessType;
    };
    saveType?;
    dashboard?;
  }) {
    const saveAsNew = this.state.saveMode === SaveModes.SaveNew || !this.hasSelectedEntity;
    if (!saveType) {
      if (saveAsNew) {
        saveType = this.hasSelectedEntity ? SaveTypeTrackingProperty.SaveAsNew : SaveTypeTrackingProperty.New;
      } else {
        saveType = SaveTypeTrackingProperty.Existing;
      }
    }

    const reportData = {
      name: this.params.selectedEntityName,
      description: this.params.selectedEntityDescription,
      saveAsNew,
      dashboard: dashboard || this.params.selectedBookmarkDashboard,
      ...(newReportData || {}),
    };

    this.dispatchEvent(new CustomEvent(`save`, {detail: reportData}));

    this.update({
      // if we are saving as new, we are going to open the title for editing immediately after
      ...(!saveAsNew ? {isEditingTitle: false} : {}),
      // we update newReportData.name to update the report title before the save API returns
      ...(newReportData?.name ? {newReportName: newReportData.name} : {}),
    });
    const mainDashboard = this.helpers.getBoardsMainDashboard();
    const redirectDashboard = this.helpers.getBoardsRedirectDashboard();
    this.track(TrackingEvent.ReportSaved, {
      'is owner': saveAsNew || this.isOwnerUser(),
      name: this.params.selectedEntityName,
      description: this.params.selectedEntityDescription,
      'Save Type': saveType,
      ...(mainDashboard ? {'dashboard id': mainDashboard.id} : {}),
      ...(redirectDashboard ? {'redirect dashboard id': redirectDashboard.id} : {}),
      'card id': `report-${this.params.selectedEntityId}`,
      'Can save existing': !this.isSaveButtonDisabled(),
    });

    // This opens a temporary Share Modal until save API finishes and returns. Once actual save completes via API,
    // another Share Modal will trigger (in attributeChangedCallback) w/the correct bookmark-id
    if (this.state.openShareUponSave) {
      promptSharing({
        type: this.helpers.getSharableEntityType(),
        // if a bookmark is already selected, we are editing an existing report.
        id: this.params.selectedEntityId,
        'project-id': this.params.projectId,
        disabled: this.hasSelectedEntity ? false : true,
      });
    }
  }

  getHeaderActionItemsSlots() {
    const controlItems = this.getControlItems();
    return controlItems.filter((item) => Object.prototype.hasOwnProperty.call(item, `slot`));
  }

  private getControlItems(): SectionItem[] {
    let controlItems = this.params.controlItems;
    if (this.isReportModeEnabled()) {
      controlItems = [...controlItems, {slot: `boardsDuplicate`}];
    }
    return controlItems as SectionItem[];
  }

  isOwnerUser() {
    return this.params.userId === this.params.selectedEntityCreator?.id;
  }

  getCurrentProject() {
    return this.getContext(`currentProject`).get().value;
  }

  private getMoveOverflowItem() {
    if (
      getEditCapability(this.helpers.getBoardsMainDashboard()).enableEdit &&
      isNestedDashboardsEnabled(this.getCurrentProject())
    ) {
      const sharedMoveToParams = {
        label: this.t(`reports.actions.move-to`),
        icon: `share`,
        name: `move`,
        direction: Directions.Left,
      };
      return getBoardsDashboardSectionItem({
        ...sharedMoveToParams,
        placeholder: this.t(`reports.actions.move-to-board-placeholder`),
        quickCreateSaveDashboardCopy: this.t(`reports.actions.move-to-board`),
        dashboardController: this.dashboardController,
        recentDashboardIds: [],
        offset: 10,
        fixedParent: true,
        showProjectSelector: false,
        showMoveOutButton: false,
        hideDashboardList: false,
        excludedDashboards: this.helpers.getBoardsMainDashboard() ? [this.helpers.getBoardsMainDashboard().id] : [],
        enableNestedDashboardsRestrictions: false,
      });
    }
    return null;
  }

  getHeaderActionOverflowItems(): Section[] {
    const renameItem = {
      icon: `edit`,
      value: HeaderAction.Rename,
      label: this.t(`reports.actions.rename`),
      elref: `reportOverflowEditButton`,
    };
    // TODO right now the deletion logic is on each report, when the users report combines reports and cohorts without
    // having separate logic to delete separately we can move bookmark deletion logic back to this component
    // rather than separating it all out
    const deleteItem = {
      icon: `trashcan`,
      value: HeaderAction.Delete,
      label: this.t(`reports.actions.delete`),
      elref: `reportDeleteButton`,
    };

    const REFRESH_ACTION = Object.freeze({
      icon: `refresh`,
      value: HeaderAction.Refresh,
      label: this.t(`reports.actions.refresh-data`),
      ...(this.isReportModeEnabled() && this.params.computedAt
        ? {subLabel: getLastRefreshedTimeLabel(moment(this.params.computedAt))}
        : []),
      elref: `reportRefreshButton`,
    });

    if (this.isReportModeEnabled()) {
      return this.buildReportModeActionOverflowSections(deleteItem, renameItem, REFRESH_ACTION);
    } else if (this.isDashboardModeEnabled()) {
      return this.buildDashboardModeActionOverflowSections(deleteItem, renameItem, REFRESH_ACTION);
    } else if (this.isCanvasModeEnabled() || this.isExperimentModeEnabled()) {
      return this.buildBasicModeActionOverflowSections(deleteItem, renameItem);
    } else {
      // Legacy apps like Impact
      return [
        {
          items: this.filterHiddenActions([
            ...(this.canDelete() ? [deleteItem] : []),
            ...(this.canEditExistingEntity() ? [renameItem] : []),
          ]),
        },
        ...(this.params.isQuerySamplingActionVisible ? [{items: this.getQuerySamplingOverflowItems()}] : []),
        {
          items: [REFRESH_ACTION],
        },
      ];
    }
  }

  private buildDashboardModeActionOverflowSections(
    deleteItem: SectionItem,
    renameItem: SectionItem,
    refreshAction: Readonly<SectionItem>,
  ): Section[] {
    const overflowItems = this.params.overflowControlItems;
    return [
      {
        items: this.filterHiddenActions([
          ...(this.canDelete() ? [deleteItem] : []),
          ...(this.canEditExistingEntity() ? [renameItem] : []),
          ...(this.canDuplicate()
            ? [
                {
                  label: this.t(`boards.actions.duplicate`),
                  value: HeaderAction.Duplicate,
                  icon: `duplicate`,
                },
              ]
            : []),
        ]),
      },
      {
        items: this.filterHiddenActions(this.getUndoRedoActions()),
      },
      ...overflowItems,
      {
        items: [refreshAction],
      },
    ];
  }

  private buildBasicModeActionOverflowSections(deleteItem: SectionItem, renameItem: SectionItem): Section[] {
    const overflowItems = this.params.overflowControlItems;
    return [
      {
        items: this.filterHiddenActions([
          ...(this.canDelete() ? [deleteItem] : []),
          ...(this.canEditExistingEntity() ? [renameItem] : []),
          ...(this.canDuplicate()
            ? [
                {
                  label: this.t(`boards.actions.duplicate`),
                  value: HeaderAction.Duplicate,
                  icon: `duplicate`,
                },
              ]
            : []),
        ]),
      },
      ...overflowItems,
    ];
  }

  private buildReportModeActionOverflowSections(
    deleteItem: SectionItem,
    renameItem: SectionItem,
    refreshAction: Readonly<SectionItem>,
  ): Section[] {
    const overflowItems = this.params.overflowControlItems;
    const moveItem = this.getMoveOverflowItem();
    return [
      {
        items: [
          ...(this.canDelete() ? [deleteItem] : []),
          ...(this.canEditExistingEntity() ? [renameItem] : []),
          ...(this.params.duplicateReportUrl
            ? [
                {
                  label: this.t(`reports.actions.duplicate`),
                  value: HeaderAction.Duplicate,
                  icon: `duplicate`,
                  href: this.params.duplicateReportUrl,
                  target: `_blank`,
                },
              ]
            : []),
          ...this.getUndoRedoActions(),
          ...(moveItem ? [moveItem] : []),
        ],
      },
      {
        items: [
          {
            ...RESET_ACTION,
            href: getReportUrl(this.params.displayContext, {
              projectId: this.params.projectId,
              workspaceId: this.params.workspaceId,
            }),
            target: `_blank`,
            label: this.t(`reports.actions.new-report`),
          },
          ...(this.params.isCustomAlertsActionVisible ? this.getAlertItems() : []),
          ...(overflowItems?.length >= 1 ? overflowItems[0].items : []),
        ],
      },
      {
        items: [refreshAction],
      },
    ];
  }

  getRecentlyViewedDashboards() {
    const dashboards =
      this.recentlyViewedDashboardsStorage?.getItem({userId: this.params.userId, key: this.params.projectId}) || [];
    return dashboards.map((dashboard) => dashboard.id);
  }

  private isUserConsumer(): boolean {
    const userIsConsumer = !userDetailsStore.value.projects[this.params.projectId]?.permissions.includes(
      Permission.EditCreatedEntityVisibility,
    );
    return userIsConsumer && !userDetailsStore.value.isStaff;
  }

  canAccessDigests(): boolean {
    return Boolean(
      userDetailsStore.value.projects[this.params.projectId]?.permissions.includes(
        Permission.AccessDashboardEmailDigests,
      ),
    );
  }

  canSaveNewReport() {
    return this.params.canWrite && !this.params.invalidEntity;
  }

  isLegacySaveActionDisabled() {
    return this.isSaveButtonDisabled() || !this.hasUnsavedChanges || this.state.dashboardsLoading;
  }

  isSaveButtonDisabled() {
    return !this.canSave() || !this.params.canWrite;
  }

  isSaveAsNewButtonDisabled() {
    return this.params.invalidEntity || !this.hasSelectedEntity;
  }

  openCustomAlertPage() {
    window.open(`/settings/account/alert-settings/report/${this.params.selectedEntityId}`);
  }

  trackDuplicateButtonClick() {
    const mainDashboard = this.helpers.getBoardsMainDashboard();
    const redirectDashboard = this.helpers.getBoardsRedirectDashboard();
    this.track(TrackingEvent.DuplicateClicked, {
      'is owner': this.isOwnerUser(),
      name: this.params.selectedEntityName,
      description: this.params.selectedEntityDescription,
      'Save Type': SaveTypeTrackingProperty.Duplicated,
      ...(mainDashboard ? {'dashboard id': mainDashboard.id} : {}),
      ...(redirectDashboard ? {'redirect dashboard id': redirectDashboard.id} : {}),
      'card id': `report-${this.params.selectedEntityId}`,
      'Can save existing': !this.isSaveButtonDisabled(),
    });
  }

  canSave() {
    return !this.params.invalidEntity && (this.params.canSave || this.helpers.allowSuperAdminOverride());
  }

  canEditExistingEntity() {
    // this is different from canDelete bc we should be able to delete even if we can't write, as long as we can save
    return (
      this.hasSelectedEntity &&
      ((this.params.canWrite && this.params.canSave) || this.helpers.allowSuperAdminOverride())
    );
  }

  canDelete() {
    return this.hasSelectedEntity && (this.params.canSave || this.helpers.allowSuperAdminOverride());
  }

  canDuplicate() {
    return !this.params.isTemplateDraftDashboard;
  }

  private async loadDashboardDataController(
    selectedEntityId?: number,
    originDashboard?: DashboardOrigin,
    forceReload?: boolean,
  ) {
    selectedEntityId = selectedEntityId || this.params.selectedEntityId;
    originDashboard = originDashboard || this.params.originDashboard;
    if (
      (forceReload || isNil(this.extraDashboards)) &&
      this.dashboardController &&
      this.isReportModeEnabled() &&
      (selectedEntityId || originDashboard)
    ) {
      this.update({dashboardsLoading: true});

      const loadDashboardsPromise = this.dashboardController.loadDashboards().then(() => {
        this.update({dashboardsLoading: false});

        // this section is still necessary for now in the case where we are creating a new report on a dashboard but we haven't
        // populated the dashboard permissions, so we should still be preventing users who don't have the ability to edit the
        // main dashboard from being able to edit
        // TODO @tiffany remove this section after we ensure can-write is accurate or this is rewritten
        const mainDashboard = this.helpers.getBoardsMainDashboard();
        if (mainDashboard) {
          const dashboard = this.dashboardController.getDashboards().find((d) => d.id === mainDashboard.id);
          if (!dashboard) {
            //fix for when dashboardController just loads the dashboards from cache while waiting for network response and main dashboard isn't in cache repsonse yet
            this.update({
              canSaveToMainDashboard: dashboardUtils.canEditDashboard({
                allow_staff_override: mainDashboard.allow_staff_override,
                is_superadmin: mainDashboard.is_superadmin,
                can_update_basic: mainDashboard.can_update_basic,
              }),
            });
          } else {
            this.update({
              canSaveToMainDashboard: dashboardUtils.canEditDashboard(dashboard),
            });
          }
        }
      });

      const bookmarkId = selectedEntityId;
      if (bookmarkId) {
        const loadLinkedIdsPromise = bookmarkClient.getLinkedDashboardIds({
          scope: {
            projectId: this.params.projectId,
            workspaceId: this.params.workspaceId,
          },
          params: {
            id: bookmarkId,
          },
        });

        const [, extraDashboardIds] = await Promise.all([loadDashboardsPromise, loadLinkedIdsPromise]);
        // TODO @gonzalo or @tiffany: extraDashboards should be in the state since we want to update the UI every time it is updated
        this.extraDashboards = extraDashboardIds.map((id) =>
          this.dashboardController.getDashboards().find((d) => d.id === id),
        );
        // update sections and title
        this.update();
      } else {
        await loadDashboardsPromise;
      }
    }
  }

  getBoardsDashboardsSections(): Section[] {
    const mainDashboard = this.helpers.getBoardsMainDashboard();
    const bookmarkId = this.params.selectedEntityId;
    const projectId = this.params.projectId;
    const workspaceId = this.params.workspaceId;

    if (!mainDashboard) {
      return [];
    }
    return [
      {
        label: `Original Board`,
        items: [
          {
            label: mainDashboard.title,
            value: mainDashboard.title,
            isSelected: false,
            subLabel: mainDashboard.creator_name,
            href: bookmarkId
              ? getCardInDashboardHref({
                  dashboardId: mainDashboard.id,
                  bookmarkId,
                  projectId,
                  workspaceId,
                })
              : getReportUrl(`dashboards#id=${mainDashboard?.id}`, {projectId, workspaceId}),
          },
        ],
      },
      {
        items:
          this.extraDashboards
            ?.filter((d) => mainDashboard && d.id !== mainDashboard.id)
            .map((dashboard) => ({
              label: dashboard.title,
              value: dashboard.title,
              subLabel: dashboard.creator_name,
              isSelected: false,
              href: bookmarkId
                ? getCardInDashboardHref({
                    dashboardId: dashboard.id,
                    bookmarkId,
                    projectId,
                    workspaceId,
                  })
                : getReportUrl(`dashboards#id=${dashboard?.id}`, {projectId, workspaceId}),
            })) || [],
      },
    ];
  }

  /**
   * @returns list of all the dashboards where this report is present, including its master and linked dashboards
   */
  getAllDashboards(): DashboardOrigin[] {
    let dashboards = [];
    if (this.params.selectedBookmarkDashboard) {
      dashboards = [this.params.selectedBookmarkDashboard];
    }
    if (this.extraDashboards?.length > 0) {
      dashboards = [
        ...dashboards,
        ...this.extraDashboards.map((dashboard) => pick(dashboard, [`id`, `title`, `creator_name`])),
      ];
    }
    return uniqBy(dashboards, (d) => d.id);
  }

  showAvailableDashboardsDropdown() {
    // this is set whenever you click on an existing report from a dashboard or create a new report from the modal, so
    // no matter what we want to show the dropdown
    if (this.helpers.getBoardsMainDashboard()) {
      return true;
    }
    // this is set whenever you click on an existing report from a dashboard
    // if we get to this point, that means that we didn't come from the dashboard, and so in the beginning the dropdown
    // will be empty until we load all the dashboards
    return this.hasSelectedEntity && this.getAllDashboards().length > 0;
  }

  getUndoRedoActions(): SectionItem[] {
    const {canUseUndoRedo, canApplyUndo, canApplyRedo} = this.getContext(`historyManager`);
    if (!canUseUndoRedo) {
      return [];
    }

    return [
      {
        label: this.t(`reports.actions.undo`),
        icon: `undo`,
        isDisabled: canApplyUndo === false,
        sideLabel: generateShortcutLabel(`Z`, {modifiers: [getCommandKey()], joinStr: ` `}),
        value: HeaderAction.Undo,
        elref: `reportUndoButton`,
      },
      ...(canApplyRedo
        ? [
            {
              label: this.t(`reports.actions.redo`),
              icon: `redo`,
              sideLabel: generateShortcutLabel(`Z`, {modifiers: [KeyModifiers.Shift, getCommandKey()], joinStr: ` `}),
              value: HeaderAction.Redo,
              elref: `reportRedoButton`,
            },
          ]
        : []),
    ];
  }

  getHeaderActionItems(): Section {
    const saveTooltipText = this.getSaveTooltipText();
    const createdBy = {
      slot: `createdBy`,
      label: null,
      value: HeaderAction.CreatedBy,
    };

    const actions: SectionItem[] = [];
    const querySamplingAction: SectionItem[] = [];

    const subscribeAction = {
      slot: `subscribe`,
      value: HeaderAction.Subscribe,
      label: null,
    };

    if (this.isReportModeEnabled()) {
      // disabled states set in slot declaration
      actions.push(createdBy, this.getShareButtonAction(this.t(`reports.actions.copy-url`)));
      if (this.params.isQuerySamplingActionVisible) {
        querySamplingAction.push(this.getQuerySamplingHeaderItem());
      }
    } else if (this.isCanvasModeEnabled() || this.isExperimentModeEnabled()) {
      actions.push(createdBy, this.getShareButtonAction(this.t(`reports.actions.copy-url`)));
    } else if (this.isDashboardModeEnabled()) {
      // When `is-select` mode is on for the share button, remove tooltip
      // section to avoid double tooltip for the share button and the
      // menu item in mp-select
      actions.push(
        subscribeAction,
        createdBy,
        omit(this.getShareButtonAction(), `tooltip`),
        ...(this.params.canWrite ? [RESET_ACTION] : []),
      );
    } else {
      // This branch is for legacy reports like Impact

      // When `is-select` mode is on for the share button, remove tooltip
      // section to avoid double tooltip for the share button and the
      // menu item in mp-select
      actions.push(
        createdBy,
        omit(this.getShareButtonAction(), `tooltip`),
        ...(this.params.canWrite
          ? [
              RESET_ACTION,
              this.helpers.getSaveAction(),
              {
                icon: `duplicate`,
                label: this.t(`reports.actions.saving.save-new`),
                tooltip: this.params.invalidEntity ? {text: saveTooltipText} : null,
                value: HeaderAction.SaveNew,
                elref: `reportSaveNewButton`,
                isDisabled: this.params.invalidEntity,
              },
            ]
          : []),
        // In dashboards HeaderAction.Load and HeaderAction.Reset are hidden, and `alerts-button` is not given
        {
          value: HeaderAction.Load,
          label: this.t(`reports.actions.open`),
          icon: `open`,
          elref: `reportLoadButton`,
        },
        {
          slot: `alerts-button`,
          label: ``,
        },
      );
    }

    // QTS should be the last button before the overflow menu, so put it at the end of the items array
    return {
      items: [
        ...this.filterHiddenActions(actions),
        ...this.getControlItems(),
        ...this.filterHiddenActions(querySamplingAction),
      ],
    };
  }

  private getShareButtonAction(shareTooltip = null) {
    if (!this.helpers.isShareButtonEnabled()) {
      if (this.params.invalidEntity) {
        shareTooltip = this.params.invalidMessage || this.t(`reports.actions.saving.tooltips.invalid-report`);
      } else {
        const shareCapability = this.helpers.getShareCapability();
        shareTooltip = shareCapability.disabledShareReason;
      }
    }
    const shareAction = {
      slot: `sharing`,
      tooltip: {text: shareTooltip || this.t(`reports.actions.manage-sharing`)},
      label: null,
      value: HeaderAction.Share,
    };

    return shareAction;
  }

  commonTrackingProps = (): Record<string, any> => {
    return {
      project_id: this.params.projectId,
      tab: this.params.trackName,
      workspace_id: this.params.workspaceId,
    };
  };

  private track(event: string, props: Obj = {}) {
    if (this.params.trackName) {
      emitTrackEvent(this, {
        eventName: event,
        eventPrefix: `mp-query-report-header`,
        props: {
          ...props,
          ...this.commonTrackingProps(),
        },
        cancelable: true,
      });
    }
  }

  private setSuperProperties(properties: Obj = {}) {
    mixpanel.register(properties);
  }

  private filterHiddenActions(actions: Array<SectionItem<HeaderAction>>): Array<SectionItem<HeaderAction>> {
    const hiddenActions = new Set(this.params.hideActions);
    return actions.filter((action) => !hiddenActions.has(action.value));
  }

  isReportModeEnabled() {
    const allowedReports = getDashboardAllowedReports();
    const report = getReportNameFromSearchContext(this.params.displayContext);
    return allowedReports.includes(report);
  }

  isDashboardModeEnabled() {
    return this.params.displayContext === SearchDisplayContext.Dashboard;
  }

  isCanvasModeEnabled() {
    return this.params.displayContext === SearchDisplayContext.Canvas;
  }

  isExperimentModeEnabled() {
    return this.params.displayContext === SearchDisplayContext.Experiment;
  }

  getSaveTooltipText() {
    const entityTypeName = this.print(getEntityTypeName(this.params.entityType));
    let saveTooltip;
    if (entityTypeName === `report`) {
      saveTooltip = this.t(`reports.actions.saving.tooltips.cta`);
    } else {
      saveTooltip = this.t(`reports.actions.saving.tooltips.save-entity-changes`, {entity: entityTypeName});
    }

    if (this.isSaveButtonDisabled() || !this.hasUnsavedChanges) {
      if (this.params.invalidEntity) {
        saveTooltip =
          this.params.invalidMessage ||
          this.t(`reports.actions.saving.tooltips.invalid-entity`, {entity: entityTypeName});
      } else if (this.hasSelectedEntity && !this.canSave()) {
        saveTooltip = this.t(`reports.actions.saving.tooltips.view-only`);
      } else if (!this.params.canWrite || !this.state.canSaveToMainDashboard) {
        saveTooltip = this.t(`reports.actions.saving.tooltips.view-only`);
      } else if (!this.hasUnsavedChanges) {
        saveTooltip = this.t(`reports.actions.saving.tooltips.no-changes`);
      }
    }
    return saveTooltip;
  }

  // the 'originDashboard' param usually comes from the URL, so we decode the dashboard title
  private getDecodedOriginDashboard() {
    const originDashboard = this.params.originDashboard;
    if (originDashboard) {
      return {
        ...originDashboard,
        title: decodeURIComponent(originDashboard.title),
      };
    }
    return null;
  }

  private getQuerySamplingHeaderItem(): SectionItem {
    const disabledMessage = this.params.querySamplingDisabledMessage;
    const isQuerySamplingEnabled = this.params.isQuerySamplingEnabled;
    const isTourTriggered = this.params.triggerQuerySamplingTourTooltip;
    const tooltipText =
      disabledMessage ||
      (isQuerySamplingEnabled
        ? this.t(`sampling.query-sampling.disable`)
        : this.t(`sampling.query-sampling.enable-with-link`));

    const querySamplingAction = {
      slot: `toggle-query-sampling`,
      tooltip: tooltipText && !isTourTriggered ? {text: tooltipText, placement: Placement.Top} : null,
      label: null,
      value: HeaderAction.QuerySampling,
    };
    return querySamplingAction;
  }

  private getQuerySamplingOverflowItems(): SectionItem[] {
    const disabledMessage = this.params.querySamplingDisabledMessage;
    const isQuerySamplingEnabled = this.params.isQuerySamplingEnabled;
    return [
      {
        label: isQuerySamplingEnabled
          ? this.t(`sampling.query-sampling.disable`)
          : this.t(`sampling.query-sampling.enable`),
        icon: isQuerySamplingEnabled ? `filled-sampling` : `sampling`,
        highlightIcon: isQuerySamplingEnabled,
        isDisabled: !!disabledMessage,
        tooltip: disabledMessage ? {text: disabledMessage, placement: Placement.Left} : null,
        value: HeaderAction.QuerySampling,
      },
    ];
  }

  private getAlertItems() {
    if (this.helpers.isAlertFeatureGateEnabled()) {
      const exceedsMaxAlerts = this.helpers.exceedsMaxCustomAlerts();
      const tooltip: TooltipInfo = this.helpers.isAlertButtonDisabled()
        ? {text: this.helpers.getAlertsTooltipText(), placement: Placement.Left}
        : null;
      const numAlerts = this.customAlertsForProject.length;
      const metadata = {currentCustomAlerts: numAlerts};
      const upsellInfo = getUpsellInfo(
        this.getContext(`currentProject`).get()?.value,
        ModalTrigger.ALERTS,
        this.getContext(`i18n`),
        metadata,
      );

      return [
        {
          label: this.t(`reports.actions.alerts`),
          icon: `alert-normal`,
          isDisabled: this.helpers.isAlertButtonDisabled(),
          tooltip,
          subSections: [
            {
              items: [
                exceedsMaxAlerts && !upsellInfo
                  ? {
                      label: this.t(`reports.actions.max-alerts-exceeded`),
                      value: HeaderAction.IncreaseAlertLimit,
                      icon: `lightning-bolt`,
                      isDisabled: this.helpers.isAlertButtonDisabled(),
                      tooltip,
                    }
                  : {
                      label: this.t(`reports.actions.create-alert`),
                      value: HeaderAction.CreateAlert,
                      icon: `plus`,
                      isDisabled: this.helpers.isAlertButtonDisabled(),
                      tooltip,
                    },
                {
                  label: this.t(`reports.actions.manage-alerts`),
                  value: HeaderAction.ManageAlert,
                  icon: `settings`,
                  isDisabled: this.helpers.isAlertButtonDisabled(),
                  tooltip,
                },
              ],
            },
          ],
        },
      ];
    } else {
      const tooltip: TooltipInfo = {text: `Enable Alerts`, placement: Placement.Left};

      if (getUpsellInfo(this.getContext(`currentProject`).get()?.value, ModalTrigger.ALERTS, this.getContext(`i18n`))) {
        const iconRenderer = () => {
          return [
            h(`svg-icon`, {
              class: {'align-left': true},
              attrs: {
                icon: `alert-normal`,
              },
            }),
            h(`svg-icon`, {
              class: {'annotate-icon': true},
              attrs: {
                icon: `brand-upgrade`,
              },
            }),
          ];
        };

        return [
          {
            label: this.t(`reports.actions.alerts`),
            iconRenderer,
            tooltip,
            value: HeaderAction.EnableAlerts,
          },
        ];
      }
      return [
        {
          label: this.t(`reports.actions.alerts`),
          icon: `alert-normal`,
          tooltip,
          value: HeaderAction.EnableAlerts,
        },
      ];
    }
  }

  private resetPresenceMonitor(
    resourceId: number,
    entityType: EntityType.Report | EntityType.Dashboard | EntityType.Canvas | EntityType.Experiment,
  ) {
    this.presenceActivityMonitor?.stop();
    const projectId = this.params.projectId;
    if (currentProjectStore.value?.isDemo || isEmbedMode()) {
      return;
    }

    if (resourceId && projectId && entityType) {
      const callback = (users) => this.updatePresenceIcons(users);
      this.presenceActivityMonitor = getPresenceActivityMonitor({projectId, entityType, resourceId, callback}).start();
    }
  }

  private updatePresenceIcons({views, total}: PresenceResults) {
    const presenceViews: MixpanelUserItem[] = views.map(({user, timestamp}) => ({
      id: user.id,
      email: user.email,
      name: user.name,
      lastViewedUtcSeconds: timestamp,
    }));

    this.update({presenceViews, totalPresenceViews: total});
  }

  getPresenceTrackingProps() {
    const nowInSeconds = new Date().getTime() / 1000;
    const oneMonthAgoUtcSeconds = nowInSeconds - 30 * 24 * 60 * 60;

    // We exclude the creator
    const numberOfViews = this.state.presenceViews?.slice(1).length || 0;
    const numberOfRecentViews =
      this.state.presenceViews?.slice(1).filter((view) => view.lastViewedUtcSeconds >= oneMonthAgoUtcSeconds).length ||
      0;

    return {
      [TrackingEventProperty.Source]: `report-header`,
      [TrackingEventProperty.ViewerType]: this.isOwnerUser() ? `Creator` : `Viewer`,
      [TrackingEventProperty.NumberOfViews]: numberOfViews,
      [TrackingEventProperty.NumberOfRecentViewsLast30d]: numberOfRecentViews,
      [TrackingEventProperty.ResourceType]: this.params.entityType,
      [TrackingEventProperty.ResourceSubType]: this.params.displayContext,
    };
  }

  private tryToShowCreateAlertDialog() {
    // raf to make sure conditions behind alert button have settled
    requestAnimationFrame(() => {
      if (!this.helpers.isAlertButtonDisabled()) {
        this.update({isCreateAlertModalOpen: true});
      } else {
        addToast({
          message: this.helpers.getAlertsTooltipText(),
          type: ToastType.WARNING,
        });
      }
    });
  }

  get isSavingAsNew(): boolean {
    return this.params.isSavingBookmark && this.state.saveMode === SaveModes.SaveNew && !!this.params.selectedEntityId;
  }
}

export const QueryReportHeaderEl = defineAndMakeJsxFactory<JsxProps>(`mp-query-report-header`, QueryReportHeader);
export const QueryReportHeaderReact = panel2react<JsxProps>(`mp-query-report-header`, QueryReportHeader);
